import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import api from "@/lib/api";
import { formatDateTime } from "@/i18n";
import { Newspaper, Users, Image as ImageIcon, ShieldCheck, ArrowUpRight, Activity, Inbox } from "lucide-react";
import { useAuth } from "@/context/AuthContext";

export default function Dashboard() {
  const { t } = useTranslation();
  const { user } = useAuth();
  const [stats, setStats] = useState(null);
  const [activity, setActivity] = useState([]);

  useEffect(() => {
    api.get("/stats").then((r) => setStats(r.data));
    api.get("/activity?limit=8").then((r) => setActivity(r.data));
  }, []);

  const labelAction = (a) =>
    ({ created: t("admin.dashboard.actionCreated"), updated: t("admin.dashboard.actionUpdated"), deleted: t("admin.dashboard.actionDeleted") }[a] || a);
  const labelEntity = (e) =>
    ({
      news: t("admin.dashboard.entityNews"),
      member: t("admin.dashboard.entityMember"),
      album: t("admin.dashboard.entityAlbum"),
      image: t("admin.dashboard.entityImage"),
      admin: t("admin.dashboard.entityAdmin"),
      message: t("admin.dashboard.entityMessage"),
    }[e] || e);

  return (
    <div data-testid="admin-dashboard" className="space-y-10">
      <div>
        <div className="text-xs uppercase tracking-[0.3em] text-bordeaux-accent mb-2">{t("admin.dashboard.eyebrow")}</div>
        <h1 className="text-3xl sm:text-4xl font-bold tracking-tight text-slate-900">
          {t("admin.dashboard.welcome", { name: user?.name || user?.email })}
        </h1>
        <p className="text-slate-600 mt-2">{t("admin.dashboard.subtitle")}</p>
      </div>

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <StatCard label={t("admin.dashboard.statsNewsTotal")} value={stats?.news_total ?? "—"} testId="stat-news-total" />
        <StatCard label={t("admin.dashboard.statsNewsPublished")} value={stats?.news_published ?? "—"} accent testId="stat-news-published" />
        <StatCard label={t("admin.dashboard.statsNewsDrafts")} value={stats?.news_drafts ?? "—"} muted testId="stat-news-drafts" />
        <StatCard label={t("admin.dashboard.statsMembers")} value={stats?.members_total ?? "—"} testId="stat-members" />
        <StatCard label={t("admin.dashboard.statsAlbums")} value={stats?.albums_total ?? "—"} testId="stat-albums" />
        <StatCard label={t("admin.dashboard.statsImages")} value={stats?.images_total ?? "—"} testId="stat-images" />
        <StatCard label={t("admin.dashboard.statsAdmins")} value={stats?.admins_total ?? "—"} testId="stat-admins" />
        <StatCard label={t("admin.dashboard.statsMessages")} value={stats?.messages_total ?? "—"} testId="stat-messages" />
        <StatCard label={t("admin.dashboard.statsMessagesUnread")} value={stats?.messages_unread ?? "—"} accent testId="stat-messages-unread" />
      </div>

      <section>
        <h2 className="text-xs uppercase tracking-[0.3em] text-slate-500 mb-4">{t("admin.dashboard.quickActions")}</h2>
        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
          <QuickLink to="/admin/news/neu" icon={<Newspaper size={18} />} label={t("admin.dashboard.quickNews")} testId="quick-news" />
          <QuickLink to="/admin/mitglieder" icon={<Users size={18} />} label={t("admin.dashboard.quickMembers")} testId="quick-members" />
          <QuickLink to="/admin/galerie" icon={<ImageIcon size={18} />} label={t("admin.dashboard.quickGallery")} testId="quick-gallery" />
          <QuickLink to="/admin/nachrichten" icon={<Inbox size={18} />} label={t("admin.dashboard.quickMessages")} testId="quick-messages" />
          {user?.role === "super_admin" && (
            <QuickLink to="/admin/administratoren" icon={<ShieldCheck size={18} />} label={t("admin.dashboard.quickAdmins")} testId="quick-admins" />
          )}
        </div>
      </section>

      <section>
        <div className="flex items-end justify-between mb-4">
          <h2 className="text-xs uppercase tracking-[0.3em] text-slate-500">{t("admin.dashboard.recentActivity")}</h2>
          <Link to="/admin/aktivitaet" className="text-xs text-bordeaux hover:underline">{t("admin.common.viewAll")}</Link>
        </div>
        <div className="bg-white border border-slate-200 rounded-sm overflow-hidden">
          {activity.length === 0 ? (
            <div className="p-8 text-center text-slate-500 text-sm">{t("admin.dashboard.noActivity")}</div>
          ) : (
            <ul className="divide-y divide-slate-100">
              {activity.map((a) => (
                <li key={a.id} data-testid={`activity-${a.id}`} className="px-5 py-3 flex items-center justify-between text-sm">
                  <div>
                    <span className="font-medium text-slate-900">{a.user_name || a.user_email}</span>{" "}
                    <span className="text-slate-500">{labelAction(a.action)}</span>{" "}
                    <span className="text-slate-700">{labelEntity(a.entity_type)}</span>
                    {a.summary && <span className="text-slate-500"> — „{a.summary}“</span>}
                  </div>
                  <div className="text-xs text-slate-500 tabular-nums">{formatDateTime(a.timestamp)}</div>
                </li>
              ))}
            </ul>
          )}
        </div>
      </section>
    </div>
  );
}

function StatCard({ label, value, accent, muted, testId }) {
  return (
    <div
      data-testid={testId}
      className={`bg-white border border-slate-200 rounded-sm p-6 ${accent ? "border-l-4 border-l-bordeaux" : muted ? "border-l-4 border-l-slate-300" : ""}`}
    >
      <div className="text-xs uppercase tracking-widest text-slate-500">{label}</div>
      <div className="mt-3 text-3xl font-semibold text-slate-900 tabular-nums">{value}</div>
    </div>
  );
}

function QuickLink({ to, icon, label, testId }) {
  return (
    <Link to={to} data-testid={testId} className="group flex items-center justify-between bg-white border border-slate-200 rounded-sm p-5 hover:border-bordeaux transition-colors">
      <div className="flex items-center gap-3 text-slate-700 group-hover:text-bordeaux">
        {icon}
        <span className="font-medium">{label}</span>
      </div>
      <ArrowUpRight size={16} className="text-slate-400 group-hover:text-bordeaux transition-colors" />
    </Link>
  );
}
